import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Plus,
  FolderLock,
  MessageSquare,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSession } from "@/hooks/useSession";

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export default function Sidebar({
  isCollapsed,
  onToggleCollapse,
}: SidebarProps) {
  const { user, signOut } = useSession();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const navItems = [
    { path: "/", label: "Dashboard", icon: LayoutDashboard },
    { path: "/create", label: "Create", icon: Plus },
    { path: "/ciphers", label: "Manage", icon: FolderLock },
    { path: "/messages", label: "EM-Crypter", icon: MessageSquare },
    { path: "/verification", label: "Verify", icon: ShieldCheck },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 md:hidden bg-white shadow-md"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
      >
        {isMobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>

      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 h-full bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 text-white transition-all duration-300 ease-in-out z-40 shadow-2xl",
          isCollapsed ? "w-20" : "w-72",
          isMobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        )}
      >
        <div className="h-20 border-b border-purple-800/50 flex items-center justify-between px-4">
          {!isCollapsed && (
            <div className="flex items-center space-x-2">
              <span className="text-3xl" role="img" aria-label="Detective">
                🕵️
              </span>
              <h1 className="text-xl font-bold bg-gradient-to-r from-pink-400 to-purple-300 bg-clip-text text-transparent">
                Cryptolect
              </h1>
            </div>
          )}
          {isCollapsed && (
            <span
              className="text-3xl mx-auto"
              role="img"
              aria-label="Detective"
            >
              🕵️
            </span>
          )}
        </div>

        <nav className="flex-1 py-6 overflow-y-auto">
          <ul className="space-y-2 px-3">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setIsMobileOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                      active
                        ? "bg-gradient-to-r from-pink-500 to-purple-500 shadow-lg"
                        : "hover:bg-purple-800/50",
                      isCollapsed && "justify-center",
                    )}
                  >
                    <Icon
                      className={cn("h-5 w-5", active && "animate-pulse")}
                    />
                    {!isCollapsed && (
                      <span className="font-medium">{item.label}</span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="border-t border-purple-800/50 p-4">
          {user ? (
            <div
              className={cn(
                "flex flex-col items-stretch gap-2",
                isCollapsed && "items-center",
              )}
            >
              {!isCollapsed && (
                <p className="text-xs text-purple-200 truncate">
                  {user.email ?? user.name ?? user.id}
                </p>
              )}
              <Button
                variant="ghost"
                onClick={() => void signOut()}
                className={cn(
                  "w-full text-white hover:bg-purple-800/50",
                  isCollapsed && "px-2",
                )}
              >
                {isCollapsed ? <X className="h-4 w-4" /> : "Sign Out"}
              </Button>
            </div>
          ) : (
            <Link to="/login" onClick={() => setIsMobileOpen(false)}>
              <Button
                variant="ghost"
                className={cn(
                  "w-full text-white hover:bg-purple-800/50",
                  isCollapsed && "px-2",
                )}
              >
                Sign In
              </Button>
            </Link>
          )}
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="absolute -right-3 top-24 bg-purple-900 hover:bg-purple-800 rounded-full shadow-lg border-2 border-purple-700 hidden md:flex"
          onClick={onToggleCollapse}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </aside>
    </>
  );
}
